import {
  AMMO_LABEL,
  HINTS,
  PERFECT_COPY,
  SNOW_LABEL,
  TUTORIAL_COPY,
  coachGeometry,
  tutorialLabel,
  jarFill,
  COUNTDOWN_COPY,
  countdownStep,
  lapFraction,
  matchHudGeometry,
  matchRound,
  overlayKind,
  r3Hint,
  trayModel,
  TutorialState,
} from "../ui/match-hud";
import { FEEDBACK_TICKS, POPUP_TICKS, type HudFx } from "../ui/hud-fx";

export interface MatchHudFrame {
  phase: string;
  snow: number;
  ammo: number;
  tick: number;
  width: number;
  height: number;
  controlsTop: number;
  hidden: boolean;
  reduced: boolean;
  rollRadians?: number;
  /** A player-targeted snowball is in the air. */
  incoming?: boolean;
  fx?: HudFx;
  /** Ticks since the current phase began. */
  phaseTick?: number;
  /** First-run practice: which of the three steps is showing. */
  tutorialStep?: 1 | 2 | 3;
  /** Bottom of the pause button row; the coach card hangs below it. */
  headerBottom?: number;
  /** Screen y (CSS px) of the opponent's hat top, when it is projected. */
  opponentTop?: number;
  /** R3 action circles to ring in gold during step 3. */
  actionRects?: { x: number; y: number; width: number; height: number }[];
}

const INK = "#2A2440";
const PILL = "#2A245A";
/** Game Design: SNOW / "−1 ball of snow" pills are the mockup blue. */
const SNOW_PILL = "#1F6BFF";
const GOLD = "#FFD640";
const ball = (r = 13) =>
  `<circle cx="20" cy="20" r="${r}" fill="white" stroke="${INK}" stroke-width="2.5"/><path d="M10 24a11 11 0 0 0 19 1" fill="none" stroke="#D9D2FF" stroke-width="4" stroke-linecap="round"/><circle cx="15" cy="15" r="3" fill="#F3F0FF"/>`;
const star = (x: number, y: number, r: number) =>
  `<path d="M${x} ${y - r}L${x + r * 0.3} ${y - r * 0.3}L${x + r} ${y}L${x + r * 0.3} ${y + r * 0.3}L${x} ${y + r}L${x - r * 0.3} ${y + r * 0.3}L${x - r} ${y}L${x - r * 0.3} ${y - r * 0.3}Z" fill="${GOLD}" stroke="${INK}" stroke-width="1.5" stroke-linejoin="round"/>`;
const glyph = (round: number, h: number) => {
  const cy = h / 2,
    r = Math.min(36, h / 2 - 8);
  return [
    "",
    `<path d="M92 ${cy}H212"/><path d="M100 ${cy - 12}L86 ${cy}L100 ${cy + 12} M204 ${cy - 12}L218 ${cy}L204 ${cy + 12}" stroke-dasharray="none"/>`,
    `<circle cx="152" cy="${cy}" r="${r}"/><path d="M${152 + r} ${cy - 20}L${162 + r} ${cy - 8}L${146 + r} ${cy - 4}" stroke-dasharray="none"/>`,
    `<path d="M152 ${h - 8}V12"/><path d="M138 ${24}L152 8L166 ${24}" stroke-dasharray="none"/>`,
  ][round]!;
};

const FEEDBACK_BLOCK_H = 88;
/** Landscape: the popup may not rise into the ROUND label (ends y≈104). */
const LANDSCAPE_FEEDBACK_MIN_TOP = 106;
/**
 * Top and scale of the BLOCKED!/DODGED! block. Portrait: the 88·s block hangs
 * 8·s above the opponent's hat (top = hat − 96·s). Landscape has only ≈90 px
 * between the ROUND label and the hat, so the block shrinks (never below 0.7)
 * until it fits; top = max(106, hat − 96·k).
 */
export function feedbackBlock(
  opponentTop: number | undefined,
  height: number,
  s: number,
  landscape: boolean,
): { top: number; scale: number } {
  if (landscape) {
    const hat = opponentTop ?? 0.53 * height;
    const k = Math.min(
      s,
      Math.max(0.7, (hat - LANDSCAPE_FEEDBACK_MIN_TOP) / 96),
    );
    return {
      top: Math.max(LANDSCAPE_FEEDBACK_MIN_TOP, hat - 96 * k),
      scale: k,
    };
  }
  return {
    top: (opponentTop ?? Math.round(0.48 * height)) - 96 * s,
    scale: s,
  };
}

export class MatchHUD {
  readonly root = document.createElement("div");
  private tutorial: TutorialState;
  private previousRound = 0;
  private previousSnow = 0;
  private previousAmmo = 0;
  private snowAt = -1000;
  private snowDelta = 0;
  private packAt = -1000;
  private progress = 0;
  private forceFresh: boolean;
  private lastMarkup = "";
  /** Called when the Skip pill is tapped during practice. */
  onSkip?: () => void;
  constructor() {
    const query = new URLSearchParams(location.search);
    const qa = query.has("qa") || query.has("qaSeed");
    const override = query.get("qaTutorial") ?? (qa ? "fresh" : null);
    this.forceFresh = qa && override === "fresh";
    let storage;
    try {
      storage = localStorage;
    } catch {
      /* Unavailable in private contexts. */
    }
    this.tutorial = new TutorialState(storage, override);
    this.root.id = "match-hud";
    this.root.style.cssText =
      "position:absolute;inset:0;pointer-events:none;z-index:11;overflow:hidden;display:none";
    this.root.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement | null)?.closest?.('[data-hud="skip"]')) {
        e.stopPropagation();
        this.onSkip?.();
      }
    });
    document.body.append(this.root);
  }
  input(type: string, radians?: number): void {
    if (type === "rollProgress") this.progress = lapFraction(radians ?? 0);
  }
  finish(): void {
    this.tutorial.finish();
  }
  hide(): void {
    this.root.style.display = "none";
    if (this.lastMarkup) {
      // Drop stale cards (coach, handoff) so a later match never inherits them.
      this.root.replaceChildren();
      this.lastMarkup = "";
    }
  }
  render(f: MatchHudFrame): boolean {
    const { tick, width, height, reduced } = f;
    const round = matchRound(f.phase);
    const overlay = overlayKind(f.phase);
    if (overlay && !f.hidden) {
      if (!countdownStep(f.phase, f.phaseTick ?? 0)) {
        // R1 banner before its GO! disc: the canvas ribbon draws alone.
        this.hide();
        return false;
      }
      this.renderOverlay(f, overlay);
      return false;
    }
    if (!round || f.hidden) {
      this.hide();
      return false;
    }
    if (round !== this.previousRound) {
      this.previousRound = round;
      this.previousSnow = f.snow;
      this.previousAmmo = f.ammo;
      this.snowAt = -1000;
      this.packAt = -1000;
    }
    if (f.snow !== this.previousSnow) {
      this.snowDelta = f.snow - this.previousSnow;
      this.snowAt = tick;
      this.previousSnow = f.snow;
      if (f.snow > 0 && this.snowDelta > 0 && round === 1)
        this.tutorial.success(round);
    }
    if (f.ammo !== this.previousAmmo) {
      if (f.ammo > this.previousAmmo) {
        this.packAt = tick;
        this.tutorial.success(round);
      }
      this.previousAmmo = f.ammo;
    }
    if (round === 2 && f.fx?.packAt != null) this.tutorial.success(round);
    const step = f.tutorialStep;
    const tutorial = !!step || this.forceFresh || this.tutorial.visible(round);
    const coach = step
      ? coachGeometry(width, height, f.headerBottom ?? 0, step)
      : null;
    const g = matchHudGeometry(
        width,
        height,
        f.controlsTop,
        coach ? coach.card.y + coach.card.height : 0,
        f.headerBottom ?? 0,
      ),
      s = g.scale,
      landscape = width > height,
      fx = f.fx;
    const pos = (r: { x: number; y: number; width: number; height: number }) =>
      `left:${r.x}px;top:${r.y}px;width:${r.width}px;height:${r.height}px;`;
    const time = reduced ? 1 : (tick % 72) / 72;
    const zh = g.zone.height / s,
      cy = zh / 2;
    const finger =
      round === 1
        ? { x: 70 + 160 * time, y: cy, r: -8 }
        : round === 2
          ? {
              x: 152 + 20 * Math.cos(time * 2 * Math.PI),
              y: cy + 20 * Math.sin(time * 2 * Math.PI),
              r: 6,
            }
          : { x: 136, y: zh - 8 - (zh - 20) * time, r: -10 };
    const trail =
      round === 1
        ? `M70 ${cy}Q150 ${cy - 6} 230 ${cy}`
        : round === 2
          ? `M152 ${cy + 20}A20 20 0 1 1 170 ${cy - 8}`
          : `M130 ${zh - 6}V12`;
    const empty = round === 3 && f.ammo <= 0;
    this.root.style.display = "block";
    this.root.dataset.round = String(round);
    this.root.dataset.ammo = String(f.ammo);
    this.root.dataset.snow = String(Math.floor(f.snow));
    this.root.dataset.tutorial = String(tutorial);
    const text = (px: number, extra = "") =>
      `font:700 ${px * s}px/${(px + 2) * s}px Fredoka;${extra}`;
    const pill = (extra = "") =>
      `box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;background:${PILL};border:${2 * s}px solid white;color:white;border-radius:${18 * s}px;${extra}`;
    const hintText =
      step === 1
        ? TUTORIAL_COPY.noTimer
        : round === 1
          ? HINTS.r1
          : round === 2
            ? f.snow < 10
              ? HINTS.notEnough
              : HINTS.r2
            : r3Hint(f.ammo, !!f.incoming);

    const zone = `<div data-hud="zone" data-glyph="${["", "↔", "↻", "↑"][round]}" class="gesture-zone" style="${pos(g.zone)}opacity:${empty ? 0.45 : 1};border-radius:${(landscape ? 28 : 24) * s}px;border-width:${1.5 * s}px;backdrop-filter:blur(${6 * s}px)"><svg viewBox="0 0 304 ${zh}" style="width:100%;height:100%"><g stroke="rgba(255,255,255,.85)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="2 12" fill="none">${glyph(round, zh)}</g>${tutorial ? `<defs><linearGradient id="gesture-trail"><stop stop-color="white" stop-opacity="0"/><stop offset="1" stop-color="white" stop-opacity=".95"/></linearGradient></defs><path d="${trail}" stroke="url(#gesture-trail)" stroke-width="14" fill="none" stroke-linecap="round"/><g transform="translate(${finger.x} ${finger.y})"><circle r="24" fill="white" opacity=".25"/><circle r="16" fill="white" opacity=".55"/><g transform="rotate(${finger.r}) translate(-22 -8)"><path d="M22 6c0-6 10-6 10 0v24c2-4 10-3 10 2 2-3 9-2 9 3v16c0 12-8 21-20 21S11 66 8 56L3 40c-2-5 4-9 8-5l7 9V6z" fill="rgba(255,255,255,.88)" stroke="${INK}" stroke-opacity=".55" stroke-width="2.5" stroke-linejoin="round"/></g></g>` : ""}</svg></div>`;

    // R1/R2 SNOW jar: fill = snow / 120, a tick every 10 snow.
    const J = g.jar,
      innerH = J.height - 6 * s,
      fill = jarFill(f.snow);
    const fillTop = J.y + 3 * s + innerH * (1 - fill);
    const ticks = Array.from({ length: 12 }, (_, i) => {
      const y = J.y + 3 * s + innerH * (1 - (i + 1) / 12);
      return `<i data-tick style="position:absolute;left:${J.x + J.width - (3 + (i % 2 ? 8 : 12)) * s}px;top:${y - 1 * s}px;width:${(i % 2 ? 8 : 12) * s}px;height:${2 * s}px;background:${INK};opacity:.8"></i>`;
    }).join("");
    const snowAge = tick - this.snowAt;
    const jar =
      round <= 2
        ? `<div data-hud="jar" data-fill="${fill.toFixed(3)}" data-ticks="12" style="position:absolute;${pos(J)}box-sizing:border-box;border:${3 * s}px solid ${INK};border-radius:${14 * s}px;background:rgba(42,36,90,.28);overflow:hidden"><div data-jar-fill style="position:absolute;left:0;right:0;bottom:0;height:${fill * innerH}px;background:linear-gradient(#EAF2FF,#7FB0FF);border-top:${2 * s}px solid ${INK}"></div></div>${ticks}
    <div data-hud="jar.label" style="position:absolute;left:${J.x + J.width / 2 - 38 * s}px;top:${J.y - 32 * s}px;width:${76 * s}px;height:${26 * s}px;display:flex;justify-content:center"><span style="${pill(`${text(16)}padding:0 ${10 * s}px;background:${SNOW_PILL}`)}">${SNOW_LABEL}</span></div>
    <div data-hud="jar.value" style="position:absolute;left:${J.x - 12 * s}px;top:${J.y + J.height + 6 * s}px;width:${J.width + 24 * s}px;height:${26 * s}px;display:flex;justify-content:center"><span style="${pill(`${text(16)}padding:0 ${10 * s}px;background:white;color:${INK};border-color:${INK}`)}">${Math.floor(f.snow)}</span></div>
    ${round === 1 && snowAge < 36 && this.snowDelta > 0 ? `<div data-hud="jar.pop" style="position:absolute;right:${width - J.x + 6 * s}px;top:${fillTop - 14 * s - (reduced ? 0 : snowAge / 2) * s}px;opacity:${1 - snowAge / 36};color:white;-webkit-text-stroke:${2 * s}px ${INK};paint-order:stroke fill;${text(22)}">+${this.snowDelta}</div>` : ""}
    ${round === 2 && tick - this.packAt < POPUP_TICKS && this.packAt >= 0 ? `<div data-hud="jar.note" style="position:absolute;right:${width - J.x + 8 * s}px;top:${J.y + 20 * s}px;"><span style="${pill(`${text(16)}padding:0 ${10 * s}px;height:${26 * s}px;background:${SNOW_PILL}`)}">−1 ball of snow</span></div>` : ""}`
        : "";

    // R2 tray: slots = balls packed + balls the snow on hand can still pack.
    const T = g.tray,
      tm = trayModel(f.snow, f.ammo),
      slot = Math.min(
        26 * s,
        (T.width - 84 * s - 3 * s * Math.max(0, tm.total - 1)) /
          Math.max(1, tm.total),
      );
    const tray =
      round === 2
        ? `<div data-hud="tray" data-slots="${tm.total}" data-filled="${tm.filled}" style="position:absolute;${pos(T)}box-sizing:border-box;background:white;border:${3 * s}px solid ${INK};border-radius:${24 * s}px;box-shadow:0 ${4 * s}px ${INK};display:flex;align-items:center;padding:0 ${12 * s}px;gap:${10 * s}px;color:${INK}"><span data-tray-count style="${text(24)}min-width:${44 * s}px">×${f.ammo}</span><span style="display:flex;gap:${3 * s}px;align-items:center;flex:1;justify-content:flex-start">${Array.from(
            { length: tm.total },
            (_, i) =>
              i < tm.filled
                ? `<svg data-slot="full" viewBox="0 0 40 40" style="width:${slot}px;height:${slot}px;flex:none">${ball(16)}</svg>`
                : `<svg data-slot="open" viewBox="0 0 40 40" style="width:${slot}px;height:${slot}px;flex:none"><circle cx="20" cy="20" r="15" fill="none" stroke="${INK}" stroke-opacity=".4" stroke-width="3" stroke-dasharray="4 5"/></svg>`,
          ).join("")}</span></div>`
        : "";
    const perfectAge = fx?.perfectAt != null ? tick - fx.perfectAt : 9999;
    const perfect =
      round === 2 && perfectAge >= 0 && perfectAge < POPUP_TICKS
        ? `<div data-hud="perfect" style="position:absolute;left:0;right:0;top:${g.zone.y - 46 * s - (reduced ? 0 : Math.min(perfectAge, 24) / 2) * s}px;display:flex;justify-content:center;opacity:${Math.min(1, (POPUP_TICKS - perfectAge) / 18)}"><span style="${pill(`${text(16)}height:${30 * s}px;padding:0 ${14 * s}px;color:${GOLD};border-color:${GOLD};gap:${6 * s}px`)}"><svg viewBox="-10 -10 20 20" style="width:${14 * s}px;height:${14 * s}px">${star(0, 0, 8)}</svg>${PERFECT_COPY}<svg viewBox="-10 -10 20 20" style="width:${14 * s}px;height:${14 * s}px">${star(0, 0, 8)}</svg></span></div>`
        : "";

    // R3 ammo pill sits between the action circles.
    const A = g.counter;
    const ammoPill =
      round === 3
        ? `<div data-hud="ammo" data-count="${f.ammo}" style="position:absolute;${pos(A)}box-sizing:border-box;background:white;border:${3 * s}px solid ${INK};border-radius:${22 * s}px;box-shadow:0 ${4 * s}px ${INK};display:flex;align-items:center;justify-content:center;gap:${6 * s}px;color:${INK};${text(24)}"><svg data-ammo-ball viewBox="0 0 40 40" style="width:${34 * s}px;height:${34 * s}px;flex:none;opacity:${empty ? 0.55 : 1}">${ball()}</svg><span data-ammo-count style="opacity:${empty ? 0.55 : 1}">×${f.ammo}</span></div>
    <div data-hud="ammo.label" style="position:absolute;left:${A.x}px;top:${A.y + A.height + 6 * s}px;width:${A.width}px;display:flex;justify-content:center"><span style="${text(16, `color:${INK};white-space:nowrap`)}">${AMMO_LABEL}</span></div>`
        : "";

    // R3 block / dodge result banner + chips.
    const fb = fx?.feedback;
    const fbAge = fb ? tick - fb.at : 9999;
    // Game Design round 2: the 88 px block (52 pill + 8 gap + 28 chip) hangs 8 px
    // above the opponent's hat so it never covers either snowman.
    const { top: feedbackTop, scale: k } = feedbackBlock(
      f.opponentTop,
      height,
      s,
      landscape,
    );
    const chipBottom = feedbackTop + FEEDBACK_BLOCK_H * k;
    const textK = (px: number, extra = "") =>
      `font:700 ${px * k}px/${(px + 2) * k}px Fredoka;${extra}`;
    const feedback =
      round === 3 && fb && fbAge >= 0 && fbAge < FEEDBACK_TICKS
        ? `<div data-hud="feedback" data-kind="${fb.kind}" style="position:absolute;left:0;right:0;bottom:${f.height - chipBottom}px;display:flex;flex-direction:column;align-items:center;gap:${8 * k}px;opacity:${Math.min(1, (FEEDBACK_TICKS - fbAge) / 18)}">${
            fb.kind === "early"
              ? `<span data-chip style="${pill(`${textK(16)}height:${30 * k}px;padding:0 ${14 * k}px;background:#FF8A3D;color:${INK};border-color:${INK}`)}">Too early</span>`
              : `<span data-banner style="box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;height:${52 * k}px;padding:0 ${28 * k}px;background:#1F6BFF;border:${3 * k}px solid ${INK};border-radius:${26 * k}px;${textK(28, "color:white;letter-spacing:1px")}">${fb.kind === "blocked" ? "BLOCKED!" : "DODGED!"}</span><span data-chip style="${pill(`${textK(16)}height:${28 * k}px;padding:0 ${14 * k}px;background:white;color:#16795F;border-color:${INK}`)}">0 damage</span>`
          }</div>`
        : "";

    const hint =
      step === 3 && !f.incoming && f.ammo > 0
        ? ""
        : `<div data-hud="hud.hint" style="position:absolute;${pos(g.caption)}display:flex;justify-content:center;align-items:center;text-align:center"><span data-hint-pill style="${pill(`height:100%;${text(16)}padding:0 ${14 * s}px`)}">${hintText}</span></div>`;

    const markup =
      (coach ? this.spotlight(f, g, step!) : "") +
      zone +
      jar +
      tray +
      perfect +
      ammoPill +
      feedback +
      hint +
      (coach ? this.coachCard(f, g, coach, step!) : "");
    if (markup !== this.lastMarkup) {
      this.root.innerHTML = markup;
      this.lastMarkup = markup;
    }
    return tutorial;
  }

  /** Practice dim with a lit hole over the thing to use. */
  private spotlight(
    f: MatchHudFrame,
    g: ReturnType<typeof matchHudGeometry>,
    step: 1 | 2 | 3,
  ): string {
    const pad = 8 * g.scale;
    // Step 3 lights the whole control row; steps 1-2 light the gesture box.
    const hole =
      step === 3
        ? {
            x: 0,
            y: g.zone.y - pad,
            width: f.width,
            height: f.height - (g.zone.y - pad),
          }
        : {
            x: g.zone.x - pad,
            y: g.zone.y - pad,
            width: g.zone.width + 2 * pad,
            height: g.zone.height + 2 * pad,
          };
    const ring =
      step === 3
        ? (f.actionRects ?? [])
            .map(
              (r) =>
                `<i data-hud="tutorial.ring" style="position:absolute;left:${r.x - 6 * g.scale}px;top:${r.y - 6 * g.scale}px;width:${r.width + 12 * g.scale}px;height:${r.height + 12 * g.scale}px;box-sizing:border-box;border-radius:50%;border:${4 * g.scale}px solid ${GOLD};box-shadow:0 0 ${14 * g.scale}px ${GOLD}"></i>`,
            )
            .join("")
        : "";
    const jarGlow =
      step === 1
        ? `<i data-hud="jar.glow" style="position:absolute;left:${g.jar.x - 6 * g.scale}px;top:${g.jar.y - 6 * g.scale}px;width:${g.jar.width + 12 * g.scale}px;height:${g.jar.height + 12 * g.scale}px;box-sizing:border-box;border-radius:${18 * g.scale}px;border:${3 * g.scale}px solid ${GOLD};box-shadow:0 0 ${16 * g.scale}px ${GOLD}"></i>`
        : "";
    return `<div data-hud="spotlight" style="position:absolute;left:${hole.x}px;top:${hole.y}px;width:${hole.width}px;height:${hole.height}px;border-radius:${28 * g.scale}px;box-shadow:0 0 0 ${Math.max(f.width, f.height)}px rgba(42,36,90,.32)"></div>${ring}${jarGlow}`;
  }

  private coachCard(
    f: MatchHudFrame,
    g: ReturnType<typeof matchHudGeometry>,
    coach: ReturnType<typeof coachGeometry>,
    step: 1 | 2 | 3,
  ): string {
    const s = g.scale,
      C = coach.card,
      copy = TUTORIAL_COPY.steps[step - 1]!;
    const hit = Math.max(48, 48 * s);
    return `<div data-hud="coach" data-step="${step}" style="position:absolute;left:${C.x}px;top:${C.y}px;width:${C.width}px;height:${C.height}px;box-sizing:border-box;background:white;border:${3 * s}px solid ${INK};border-radius:${24 * s}px;box-shadow:0 ${4 * s}px ${INK};display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;color:#4A4668;padding-top:${6 * s}px">
      <div data-coach="title" style="font:700 ${24 * s}px/${30 * s}px Fredoka;color:${INK};margin-bottom:${6 * s}px">${copy.title}</div>
      ${copy.lines.map((l) => `<div data-coach="line" style="font:600 ${16 * s}px/${22 * s}px Fredoka">${l}</div>`).join("")}
      <div data-hud="tutorial.label" style="position:absolute;left:50%;top:${-14 * s - 3 * s}px;transform:translateX(-50%);height:${30 * s}px;padding:0 ${16 * s}px;box-sizing:border-box;display:flex;align-items:center;background:#1F6BFF;border:${3 * s}px solid white;border-radius:${15 * s}px;font:700 ${16 * s}px/${18 * s}px Fredoka;color:white;white-space:nowrap">${tutorialLabel(step)}</div>
      <div data-hud="skip" style="position:absolute;right:${6 * s}px;top:${-14 * s - hit / 2 + 15 * s}px;width:${Math.max(hit, 64 * s)}px;height:${hit}px;display:flex;align-items:center;justify-content:center;pointer-events:auto;touch-action:manipulation"><span data-coach="skip" style="height:${28 * s}px;padding:0 ${16 * s}px;display:flex;align-items:center;background:white;border:${2.5 * s}px solid ${INK};border-radius:${14 * s}px;font:700 ${16 * s}px/${18 * s}px Fredoka;color:${INK}">${TUTORIAL_COPY.skip}</span></div>
    </div>`;
  }

  /** Opening countdown, R1→R2 handoff card, and the round-3 banner digits. */
  private renderOverlay(
    f: MatchHudFrame,
    kind: "countdown" | "handoff" | "banner3" | "banner1",
  ) {
    const { width, height, reduced, tick } = f;
    const g = matchHudGeometry(
      width,
      height,
      f.controlsTop,
      0,
      f.headerBottom ?? 0,
    );
    const landscape = width > height;
    const pt = f.phaseTick ?? 0;
    const step = countdownStep(f.phase, pt)!;
    const s = landscape ? Math.min(g.scale, (height - 150) / 290) : g.scale;
    // 16 px body text never shrinks below 16 px (landscape card scale is < 1).
    const font = (px: number, extra = "") => {
      const size = px === 16 ? Math.max(16, 16 * s) : px * s;
      return `font:700 ${size}px/${size + 4 * s}px Fredoka;${extra}`;
    };
    const pill = (extra = "") =>
      `box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;background:${PILL};border:${2 * s}px solid white;color:white;border-radius:${18 * s}px;${extra}`;
    const bump = reduced ? 1 : 1 + 0.22 * Math.max(0, 1 - (pt % 60) / 14);
    const fade = reduced ? 0.45 + 0.55 * step.remaining : 1;
    const circle = (
      cx: number,
      cy: number,
      d: number,
      numeral: number,
      ring: boolean,
    ) => {
      const r = d / 2;
      return `<div data-hud="countdown" data-step="${step.label}" data-sweep="${ring && !reduced}" style="position:absolute;left:${cx - r}px;top:${cy - r}px;width:${d}px;height:${d}px;border-radius:50%;box-sizing:border-box;background:white;border:${Math.max(3, d / 34)}px solid ${INK};box-shadow:0 ${4 * s}px ${INK};display:flex;align-items:center;justify-content:center;opacity:${fade}">${
        ring
          ? `<svg viewBox="0 0 100 100" style="position:absolute;inset:0;width:100%;height:100%;transform:rotate(-90deg)"><circle data-ring cx="50" cy="50" r="42" fill="none" stroke="#2366FF" stroke-width="7" stroke-linecap="round" pathLength="100" stroke-dasharray="100" stroke-dashoffset="${reduced ? 0 : (100 * (1 - step.remaining)).toFixed(2)}"/></svg>`
          : ""
      }<span data-numeral style="position:relative;color:#2366FF;${font(numeral, `transform:scale(${bump});line-height:1`)}">${step.label}</span></div>`;
    };
    let markup = "";
    if (kind === "countdown") {
      const d = landscape ? Math.min(170 * s, height * 0.42) : 170 * s;
      const cx = width / 2,
        cy = height * (landscape ? 0.46 : 0.475);
      markup =
        circle(cx, cy, d, d * 0.57, true) +
        `<div data-hud="countdown.pill" style="position:absolute;left:0;right:0;top:${cy + d / 2 + 14 * s}px;display:flex;justify-content:center"><span style="${pill(`${font(20)}height:${42 * s}px;padding:0 ${22 * s}px;border-radius:${21 * s}px`)}">${COUNTDOWN_COPY.pill}</span></div>`;
    } else if (kind === "banner3" || kind === "banner1") {
      const d = 64 * s;
      markup = circle(width / 2, height * 0.4 + 118 * s, d, 30, false);
    } else {
      markup = this.handoffMarkup(f, g, s, circle, font, pill);
    }
    this.root.style.display = "block";
    this.root.dataset.round = "0";
    this.root.dataset.overlay = kind;
    this.root.dataset.tutorial = "false";
    if (markup !== this.lastMarkup) {
      this.root.innerHTML = markup;
      this.lastMarkup = markup;
    }
    void tick;
  }

  private handoffMarkup(
    f: MatchHudFrame,
    g: ReturnType<typeof matchHudGeometry>,
    s: number,
    circle: (
      cx: number,
      cy: number,
      d: number,
      numeral: number,
      ring: boolean,
    ) => string,
    font: (px: number, extra?: string) => string,
    pill: (extra?: string) => string,
  ): string {
    const { width, height } = f;
    const landscape = width > height;
    const snow = Math.floor(f.snow);
    const balls = Math.floor(snow / 10);
    const left = Math.max(10, width * 0.056);
    const right = landscape ? width * 0.66 : g.jar.x - 26 * g.scale;
    const cardW = landscape ? Math.min(340, right - left) : right - left;
    const cardX = landscape ? (width - cardW) / 2 : left;
    const cardH = 246 * s;
    const circleD = 56 * s;
    const stack = cardH + 12 * s + circleD;
    const cardY = landscape
      ? Math.max(66, (height - stack) / 2 + 24)
      : height * 0.358;
    const box = (label: string, inner: string) =>
      `<div style="flex:1;max-width:${100 * s}px;height:${80 * s}px;border-radius:${16 * s}px;background:#E3EBFF;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${2 * s}px"><span style="${font(15, "color:#2366FF")}">${label}</span>${inner}</div>`;
    const jar = this.jarForHandoff(f, g);
    const card = `<div data-hud="handoff" data-snow="${snow}" data-balls="${balls}" style="position:absolute;left:${cardX}px;top:${cardY}px;width:${cardW}px;height:${cardH}px;box-sizing:border-box;background:white;border:${3 * s}px solid ${INK};border-radius:${24 * s}px;box-shadow:0 ${5 * s}px ${INK};display:flex;flex-direction:column;align-items:center;justify-content:space-evenly;padding:${22 * s}px ${14 * s}px ${12 * s}px;color:${INK}">
      <span data-handoff-pill style="position:absolute;top:${-15 * s}px;${pill(`${font(16)}height:${30 * s}px;padding:0 ${18 * s}px;background:#2366FF`)}">${COUNTDOWN_COPY.roundDone}</span>
      <span data-handoff-title style="${font(24)}">${COUNTDOWN_COPY.title}</span>
      <div style="display:flex;align-items:center;gap:${10 * s}px;width:100%;justify-content:center">${box(COUNTDOWN_COPY.snow, `<span data-handoff-snow style="${font(32)}">${snow}</span>`)}<svg viewBox="0 0 24 24" style="width:${24 * s}px;height:${24 * s}px;flex:none"><path d="M3 2L22 12L3 22Z" fill="${INK}"/></svg>${box(COUNTDOWN_COPY.upTo, `<span style="display:flex;align-items:center;gap:${6 * s}px"><svg viewBox="0 0 40 40" style="width:${24 * s}px;height:${24 * s}px">${ball(15)}</svg><span data-handoff-balls style="${font(24)}">×${balls}</span></span>`)}</div>
      <span data-handoff-rule style="${font(16, "color:#4B4572")}">${COUNTDOWN_COPY.rule}</span>
      <span data-handoff-next style="${pill(`${font(16)}height:${30 * s}px;padding:0 ${16 * s}px`)}">${balls > 0 ? COUNTDOWN_COPY.next : COUNTDOWN_COPY.notEnough}</span>
    </div>`;
    return (
      jar +
      card +
      circle(
        cardX + cardW / 2,
        cardY + cardH + 12 * s + circleD / 2,
        circleD,
        30,
        false,
      )
    );
  }

  /** The R1 jar, frozen at the final snow, with a gold glow on the handoff. */
  private jarForHandoff(
    f: MatchHudFrame,
    g: ReturnType<typeof matchHudGeometry>,
  ) {
    const s = g.scale,
      J = g.jar,
      innerH = J.height - 6 * s,
      fill = jarFill(f.snow);
    const ticks = Array.from({ length: 12 }, (_, i) => {
      const y = J.y + 3 * s + innerH * (1 - (i + 1) / 12);
      return `<i data-tick style="position:absolute;left:${J.x + J.width - (3 + (i % 2 ? 8 : 12)) * s}px;top:${y - s}px;width:${(i % 2 ? 8 : 12) * s}px;height:${2 * s}px;background:${INK};opacity:.8"></i>`;
    }).join("");
    const label = (top: number, inner: string, wide = 24) =>
      `<div style="position:absolute;left:${J.x + J.width / 2 - (J.width + wide * s) / 2}px;top:${top}px;width:${J.width + wide * s}px;height:${26 * s}px;display:flex;justify-content:center">${inner}</div>`;
    return `<div data-hud="jar" data-fill="${fill.toFixed(3)}" data-ticks="12" style="position:absolute;left:${J.x}px;top:${J.y}px;width:${J.width}px;height:${J.height}px;box-sizing:border-box;border:${3 * s}px solid ${INK};border-radius:${14 * s}px;background:rgba(42,36,90,.28);overflow:hidden;box-shadow:0 0 ${10 * s}px ${4 * s}px rgba(255,214,64,.7)"><div style="position:absolute;left:0;right:0;bottom:0;height:${fill * innerH}px;background:linear-gradient(#EAF2FF,#7FB0FF);border-top:${2 * s}px solid ${INK}"></div></div>${ticks}${label(J.y - 32 * s, `<span style="${this.pillCss(s, 16, `background:${SNOW_PILL}`)}">${SNOW_LABEL}</span>`, 40)}${label(J.y + J.height + 6 * s, `<span data-hud="jar.value" style="${this.pillCss(s, 16, "background:white;color:" + INK + ";border-color:" + INK)}">${Math.floor(f.snow)}</span>`)}`;
  }

  private pillCss(s: number, px: number, extra = "") {
    return `box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;white-space:nowrap;background:${PILL};border:${2 * s}px solid white;color:white;border-radius:${18 * s}px;font:700 ${px * s}px/${(px + 2) * s}px Fredoka;padding:0 ${10 * s}px;${extra}`;
  }
}
